import React from 'react';
import { Link } from 'react-router-dom';
import {
  Calculator,
  BookOpen,
  FileSpreadsheet,
  CheckCircle,
  ArrowRight,
  Building,
  Briefcase,
  TrendingUp,
  Shield,
  Cloud,
  Database,
  BarChart3,
  MessageSquare,
  ClipboardCheck,
  Users,
  Phone,
  PhoneCall,
  HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../components/ui/accordion';
import usePageSeo from '../hooks/usePageSeo';
import JsonLd from '../components/JsonLd';
import { breadcrumbSchema, serviceSchema, faqSchema } from '../utils/seoSchemas';

import RelatedServices from '../components/RelatedServices';
const AccountingServicesUAE = () => {
  usePageSeo({
    title: 'Accounting Services in UAE | Bookkeeping & VAT Compliance',
    description:
      'Professional accounting services in UAE. Bookkeeping, VAT compliance & Corporate Tax ready financials. Trusted experts – WPAA.',
    path: '/accounting-services-uae'
  });

  const services = [
    {
      title: 'Bookkeeping Services',
      description: 'Accurate daily bookkeeping on Zoho Books & QuickBooks, tailored to your business workflow.',
      icon: BookOpen
    },
    {
      title: 'Financial Statements Preparation',
      description: 'P&L, Balance Sheet, and Cash Flow statements prepared in line with UAE & IFRS standards.',
      icon: FileSpreadsheet
    },
    {
      title: 'Accounts Reconciliation',
      description: 'Reconciliation of bank, supplier, and customer accounts to ensure book accuracy.',
      icon: ClipboardCheck
    },
    {
      title: 'VAT-Compliant Accounting',
      description: 'All transactions recorded per FTA VAT regulations for effortless return filing.',
      icon: Shield
    },
    {
      title: 'Corporate Tax Ready Financials',
      description: 'Books structured for the UAE 9% Corporate Tax — always filing-ready.',
      icon: Calculator
    },
    {
      title: 'General Ledger & Trial Balance',
      description: 'Clean general ledger maintenance with trial balance reviews every period.',
      icon: Database
    },
    {
      title: 'MIS & Management Reports',
      description: 'Custom management reports and dashboards for data-driven decision making.',
      icon: BarChart3
    }
  ];

  const whoNeedsAccounting = [
    { text: 'Startups & new businesses needing proper financial setup', icon: Briefcase },
    { text: 'Free Zone companies requiring compliance & reporting', icon: Building },
    { text: 'SMEs managing growing transaction volumes', icon: TrendingUp },
    { text: 'Service-based businesses with VAT obligations', icon: Shield },
    { text: 'Companies preparing for Corporate Tax filing', icon: Calculator }
  ];

  const process = [
    { step: '01', title: 'Initial Consultation', description: 'Understand your business model, scale and reporting requirements.' },
    { step: '02', title: 'Data Collection', description: 'Secure collection of invoices, bank statements, and receipts.' },
    { step: '03', title: 'System Setup', description: 'Zoho Books or QuickBooks configured to your chart of accounts.' },
    { step: '04', title: 'Bookkeeping & Recording', description: 'Accurate transaction entry with daily/weekly cadence.' },
    { step: '05', title: 'Monthly Reporting', description: 'Financial statements, KPI analysis, and management insights.' },
    { step: '06', title: 'Compliance Review', description: 'VAT and Corporate Tax readiness checks every reporting cycle.' }
  ];

  const tools = [
    { name: 'Zoho Books', description: 'UAE VAT compliant cloud accounting', icon: Cloud },
    { name: 'QuickBooks Online', description: 'Industry-standard financial platform', icon: Database },
    { name: 'Excel Analysis', description: 'Custom financial models & analysis', icon: FileSpreadsheet },
    { name: 'Secure Cloud Storage', description: 'Encrypted document management', icon: Shield }
  ];

  const compliancePoints = [
    'UAE VAT Regulations (FTA compliant)',
    'Corporate Tax Requirements (9%)',
    'Proper financial documentation for audits',
    'IFRS-based financial reporting standards'
  ];

  const whyChooseUs = [
    'UAE compliance expertise',
    'Experienced accounting professionals',
    'Zoho & QuickBooks specialists',
    'Accurate and timely reporting',
    'Transparent and competitive pricing',
    'End-to-end Audit | Tax | Advisory support'
  ];

  const faqs = [
    {
      q: 'Is bookkeeping mandatory in UAE?',
      a: 'Yes. Maintaining proper accounting records is required by UAE law for both VAT and Corporate Tax compliance. Records must typically be preserved for at least 5 years.'
    },
    {
      q: 'How often should accounting be updated?',
      a: 'We recommend monthly bookkeeping at a minimum. This ensures accurate VAT return filing, timely financial reporting, and faster year-end audit readiness.'
    },
    {
      q: 'Do you support Zoho Books and QuickBooks?',
      a: 'Yes — we are specialists in both platforms. We can set up, migrate, manage, and optimise your accounts on whichever system suits your business best.'
    },
    {
      q: 'Can you prepare financial statements for audits?',
      a: 'Absolutely. We prepare complete financial statements (P&L, Balance Sheet, Cash Flow) in line with UAE regulations and IFRS so they are audit-ready.'
    },
    {
      q: 'Do you help with Corporate Tax preparation?',
      a: 'Yes. Our accounting services are designed from the ground up to ensure your financials are fully ready for the UAE 9% Corporate Tax filing.'
    }
  ];

  return (
    <div className="min-h-screen">
      <JsonLd
        id="ld-acc-service"
        data={serviceSchema({
          name: 'Accounting Services in UAE',
          description:
            'Professional bookkeeping, financial statements, accounts reconciliation, VAT-compliant accounting, Corporate Tax ready financials and management reporting.',
          path: '/accounting-services-uae',
          serviceType: 'Accounting Services'
        })}
      />
      <JsonLd
        id="ld-acc-breadcrumb"
        data={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
          { name: 'Accounting Services UAE', path: '/accounting-services-uae' }
        ])}
      />
      <JsonLd id="ld-acc-faq" data={faqSchema(faqs)} />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-indigo-900 via-indigo-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-indigo-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-300 rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-2 mb-6">
              <Calculator className="text-indigo-400" size={32} />
              <span className="text-indigo-400 font-semibold text-sm uppercase tracking-wider">Accounting Services</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Accounting Services in UAE
            </h1>
            <p className="text-2xl text-indigo-200 mb-6 font-semibold">
              Professional Bookkeeping & Financial Reporting for UAE Businesses
            </p>
            <p className="text-xl text-gray-300 mb-6 leading-relaxed max-w-3xl">
              Managing your finances accurately is critical for business success in the UAE. At White Paper Accounts Auditing (WPAA), we provide reliable and compliant accounting services tailored to startups, SMEs, and growing enterprises.
            </p>
            <p className="text-lg text-gray-400 mb-8 max-w-3xl">
              From day-to-day bookkeeping to financial reporting and Corporate Tax readiness, we ensure your business stays organised, compliant, and audit-ready.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact?service=accounting">
                <Button size="lg" className="bg-indigo-500 hover:bg-indigo-600 text-white px-8 py-6 text-lg" data-testid="acc-consultation-btn">
                  Book Free Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="tel:+971501219341" data-testid="acc-call-btn">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  Call Us Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Our Accounting Services */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wider">Our Expertise</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Our Accounting Services Include</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              End-to-end accounting solutions designed to meet UAE regulatory requirements.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-2 hover:border-indigo-500 transition-all hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="bg-indigo-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="text-indigo-600" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who Needs Accounting Services */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wider">Target Clients</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Who Needs Accounting Services in UAE?</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {whoNeedsAccounting.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="bg-indigo-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="text-indigo-600" size={24} />
                    </div>
                    <p className="text-gray-700 font-medium pt-2">{item.text}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Accounting Process */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wider">How We Work</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Our Accounting Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A structured and transparent process to ensure accuracy and compliance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {process.map((item, index) => (
              <div key={index} className="relative bg-gray-50 rounded-2xl p-6 hover:bg-indigo-50 transition-colors">
                <div className="absolute -top-4 -left-4 bg-indigo-600 text-white w-14 h-14 rounded-xl flex items-center justify-center font-bold text-lg shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mt-4 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Systems */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wider">Technology</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Tools & Systems We Use</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Modern cloud-based tools to ensure efficiency and transparency.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {tools.map((tool, index) => {
              const Icon = tool.icon;
              return (
                <Card key={index} className="border-2 text-center hover:border-indigo-500 transition-all">
                  <CardContent className="p-6">
                    <div className="bg-indigo-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4 mx-auto">
                      <Icon className="text-indigo-600" size={28} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{tool.name}</h3>
                    <p className="text-sm text-gray-600">{tool.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* UAE Compliance Focus */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wider">Compliance First</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">UAE Compliance Focus</h2>
              <p className="text-lg text-gray-600 mb-8">
                Accounting in the UAE is not just bookkeeping — it's about compliance. We ensure your business meets every regulatory requirement.
              </p>
              <div className="space-y-4">
                {compliancePoints.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="text-indigo-600 flex-shrink-0" size={24} />
                    <p className="text-lg text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-2xl p-8 text-white">
              <Users className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Why Choose WPAA?</h3>
              <div className="space-y-3">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <CheckCircle size={18} className="mt-1 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wider">FAQs</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Frequently Asked Questions</h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white border border-gray-200 rounded-xl px-6 shadow-sm"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-indigo-600 hover:no-underline">
                    <div className="flex items-start gap-3">
                      <HelpCircle className="text-indigo-600 flex-shrink-0 mt-1" size={20} />
                      <span>{faq.q}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-base leading-relaxed pl-8">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

            <RelatedServices
        services={[
          { title: 'VAT Services in UAE', description: 'FTA-compliant VAT registration, filing, and reconciliation.', path: '/vat-services-uae', accent: 'blue' },
          { title: 'Corporate Tax Services', description: '9% UAE Corporate Tax registration and annual filing support.', path: '/corporate-tax-uae', accent: 'teal' },
          { title: 'SME Business Package', description: 'All-in-one accounting, VAT, CT and compliance package for UAE SMEs.', path: '/sme-business-services-uae', accent: 'amber' }
        ]}
      />

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-4xl font-bold mb-6">Get Started Today</h2>
          <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
            Let us handle your accounting while you focus on growing your business.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
            <Link to="/contact?service=accounting">
              <Button size="lg" className="bg-white text-indigo-700 hover:bg-gray-100 px-8 py-6 text-lg" data-testid="acc-cta-consultation-btn">
                Book Your FREE Consultation
              </Button>
            </Link>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" data-testid="acc-whatsapp-btn">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                WhatsApp Us
              </Button>
            </a>
          </div>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 text-indigo-100">
            <a href="tel:+971509987431" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="acc-phone-1">
              <Phone size={18} />
              <span>+971 50 998 7431</span>
            </a>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="acc-phone-2">
              <MessageSquare size={18} />
              <span>WhatsApp +971 50 121 9341</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AccountingServicesUAE;
